import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { extractErrorMessage } from '../utils/errorUtils';
import {
    PageHeader,
    Card,
    CardHeader,
    CardBody,
    Button,
    FormField,
    LoadingSpinner,
    ErrorMessage,
    SuccessMessage
} from '../components/common';

const ResetPassword = () => {
    const { userId } = useParams();
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [formData, setFormData] = useState({
        newPassword: '',
        confirmPassword: ''
    });

    useEffect(() => {
        fetchUserDetails();
    }, [userId]);

    const fetchUserDetails = async () => {
        try {
            setLoading(true);
            const response = await api.get(`/Users/${userId}`);
            setUser(response.data);
        } catch (err) {
            console.error('Failed to fetch user details:', err);
            setError(extractErrorMessage(err));
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);

        // Validate passwords
        if (!formData.newPassword) {
            setError('New password is required.');
            return;
        }
        if (formData.newPassword !== formData.confirmPassword) {
            setError('Passwords do not match.');
            return;
        }

        try {
            setSaving(true);
            // Depending on the backend expectation, we may pass the password in the body.
            // If the backend just generates a random password and emails it, it will ignore this.
            await api.post(`/Users/${userId}/reset-password`, { newPassword: formData.newPassword });
            setSuccess('Password has been successfully reset.');
            setFormData({ newPassword: '', confirmPassword: '' });
            setTimeout(() => navigate(`/users/${userId}`), 2000);
        } catch (err) {
            console.error('Failed to reset password:', err);
            setError(extractErrorMessage(err));
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <LoadingSpinner size="large" />;
    }

    return (
        <div className="space-y-6">
            <PageHeader
                title={`Reset Password for ${user?.firstName || ''} ${user?.lastName || ''}`}
                subtitle={user?.email}
                backLink={{ label: "Back to User Details", href: `/users/${userId}` }}
            />

            {error && (
                <ErrorMessage
                    message={error}
                    onDismiss={() => setError(null)}
                />
            )}

            {success && (
                <SuccessMessage
                    message={success}
                    onDismiss={() => setSuccess(null)}
                />
            )}

            <Card>
                <CardHeader
                    title="Reset User Password"
                    subtitle="Enter a new password for this user. They will be able to log in with this new password."
                />
                <CardBody>
                    <form onSubmit={handleResetPassword} className="space-y-6 max-w-md">
                        <FormField
                            label="New Password"
                            name="newPassword"
                            type="password"
                            value={formData.newPassword}
                            onChange={handleInputChange}
                            required
                        />
                        <FormField
                            label="Confirm Password"
                            name="confirmPassword"
                            type="password"
                            value={formData.confirmPassword}
                            onChange={handleInputChange}
                            required
                        />

                        <div className="flex justify-end space-x-3 pt-4 border-t border-slate-100">
                            <Button
                                type="button"
                                variant="secondary"
                                onClick={() => navigate(`/users/${userId}`)}
                                disabled={saving}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="primary"
                                loading={saving}
                            >
                                Reset Password
                            </Button>
                        </div>
                    </form>
                </CardBody>
            </Card>
        </div>
    );
};

export default ResetPassword;
